import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { MainContentChallenge } from './components/MainContent';
import { SideBarContent } from './components/SideBarContent';

import { Divider, Loader, Tabs } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import { DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import { challengeInit, getChallengesRecommended } from '@/services/challenges';
import { Maybe } from '@/types';
import {
  ChallengeInitResponse,
  ChallengeStatus,
  ChallengeUser,
  UserChallengeSearchTO,
} from '@/types/challenges';
import { GENERICS_WORDS, TEXTS } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

interface State {
  init: Maybe<ChallengeInitResponse>;
  recommendedList?: UserChallengeSearchTO[];
  pendingList: ChallengeUser[];
  playingList: ChallengeUser[];
  invitationList: ChallengeUser[];
  mounted: boolean;
  hitsJugados: number;
  fechas: any[];
}

const initialState: State = {
  init: null,
  recommendedList: [],
  pendingList: [],
  invitationList: [],
  playingList: [],
  mounted: false,
  hitsJugados: 0,
  fechas: [],
};

export const Challenge = () => {
  const [state, setState] = useState<State>(initialState);
  const toast = useToast();
  const { isMobile } = useIsDesktop();
  const { handleTabChanges, tab } = useHandleChangesTabs();
  const { tourneyData, updateTourney } = useTourney();
  useMetaData({
    title: 'Desafíos',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tabs: DefaultTabsProps['tabs'] = [
    { name: GENERICS_WORDS.challenges, disabled: false },
    {
      name: GENERICS_WORDS.results,
      disabled: false,
    },
  ];

  const MainTabs = useCallback(() => {
    return (
      <Tabs
        activeTab={tab}
        className="ml-2"
        tabs={tabs}
        onClick={handleTabChanges}
      />
    );
  }, [handleTabChanges, tab, tabs]);

  const { isRefetching: refetchingRecommended, isLoading: loadingRecommended } =
    useQuery(
      'getChallengesRecommended',
      async () => await getChallengesRecommended(),
      {
        onSuccess(data) {
          setState((prevState) => ({
            ...prevState,
            recommendedList: data.usuarios || [],
          }));
        },
        refetchOnWindowFocus: false,
        keepPreviousData: false,
      }
    );

  const {
    isLoading: isLodingInit,
    isRefetching: refetchingInit,
    refetch,
  } = useQuery('challengeInit', async () => await challengeInit(), {
    onSuccess(data) {
      if (data && data?.estado) {
        setState((prevState) => ({
          ...prevState,
          init: data || null,
          invitationList: filterDesafio(
            data.desafios,
            ChallengeStatus.PENDIENTE_PUEDE_CONFIRMAR
          ),
          playingList: filterDesafio(data.desafios, ChallengeStatus.CONFIRMADO),
          pendingList: data.desafios.filter(
            (desafio) =>
              desafio.idEstadoDesafio === ChallengeStatus.PENDIENTE ||
              desafio.idEstadoDesafio === ChallengeStatus.RECHAZADO
          ),
          hitsJugados: data?.hist_jugados || 0,
          fechas: data?.fechas || [],
          mounted: true,
        }));
        // Seteo globales para datos en componentes compartidos
        updateTourney({
          matchDay: data.fechas[data.fechas.length - 1],
          tourney: data,
          tourneysInvitations: filterDesafio(
            data.desafios,
            ChallengeStatus.PENDIENTE_PUEDE_CONFIRMAR
          ),
          tourneysPlaying: filterDesafio(
            data.desafios,
            ChallengeStatus.CONFIRMADO
          ),
          tourneysPending: data.desafios.filter(
            (desafio) =>
              desafio.idEstadoDesafio === ChallengeStatus.PENDIENTE ||
              desafio.idEstadoDesafio === ChallengeStatus.RECHAZADO
          ),
        });
      }
    },
    refetchOnWindowFocus: false,
    keepPreviousData: false,
  });

  useEffect(() => {
    if (tab === TabType.FirstTab) {
      refetch();
    }
  }, [refetch, tab]);

  const filterDesafio = (desafios: ChallengeUser[], condicion: number) => {
    return desafios.filter((desafio) => desafio.idEstadoDesafio === condicion);
  };

  // refresco pendientes de aceptacion al invitar postulacion
  const onHandleRefreshAfterInvitation = useCallback(
    async (pendings = false) => {
      try {
        const responseInitDesafio = await challengeInit();
        if (responseInitDesafio?.estado) {
          if (pendings) {
            setState((prevState) => ({
              ...prevState,
              pendingList: responseInitDesafio.desafios.filter(
                (desafio) =>
                  desafio.idEstadoDesafio === ChallengeStatus.PENDIENTE ||
                  desafio.idEstadoDesafio === ChallengeStatus.RECHAZADO
              ),
              init: responseInitDesafio,
            }));
          } else {
            setState((prevState) => ({
              ...prevState,
              playingList: filterDesafio(
                responseInitDesafio.desafios,
                ChallengeStatus.CONFIRMADO
              ),
              invitationList: filterDesafio(
                responseInitDesafio.desafios,
                ChallengeStatus.PENDIENTE_PUEDE_CONFIRMAR
              ),
              pendingList: responseInitDesafio.desafios.filter(
                (desafio) =>
                  desafio.idEstadoDesafio === ChallengeStatus.PENDIENTE ||
                  desafio.idEstadoDesafio === ChallengeStatus.RECHAZADO
              ),
              init: responseInitDesafio,
            }));
          }
        } else {
          // Mostrar el mensaje específico del backend
          if (responseInitDesafio.mensaje) {
            toast.show(responseInitDesafio.mensaje);
          } else {
            toast.show(TEXTS.defaultError);
          }
        }
        updateTourney({
          tourney: responseInitDesafio,
          tourneysInvitations: filterDesafio(
            responseInitDesafio.desafios,
            ChallengeStatus.PENDIENTE_PUEDE_CONFIRMAR
          ),
          tourneysPlaying: filterDesafio(
            responseInitDesafio.desafios,
            ChallengeStatus.CONFIRMADO
          ),
          tourneysPending: responseInitDesafio.desafios.filter(
            (desafio) =>
              desafio.idEstadoDesafio === ChallengeStatus.PENDIENTE ||
              desafio.idEstadoDesafio === ChallengeStatus.RECHAZADO
          ),
        });
      } catch (error) {
        console.error(error);
      }
    },
    [updateTourney, toast]
  );

  // effecto escucha al enviar postulacion
  useEffect(() => {
    if (tourneyData.idsPendings.length > 0 && state.mounted) {
      onHandleRefreshAfterInvitation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPendings]);

  // effecto escucha al aceptar invitaciones
  useEffect(() => {
    if (tourneyData.idsPlayings.length > 0 && state.mounted) {
      onHandleRefreshAfterInvitation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPlayings]);

  return (
    <>
      <Loader
        isVisible={
          isLodingInit ||
          loadingRecommended ||
          refetchingRecommended ||
          refetchingInit
        }
      />
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.desafio1a1}
        className="lg:px-4"
        main={() => (
          <div className="lg:pr-[111px] lg:min-h-[600px]">
            <MainContentChallenge
              handleRefresh={onHandleRefreshAfterInvitation}
              init={state}
              tabSelected={tab}
              tabs={<MainTabs />}
            />
          </div>
        )}
        mobile={{
          leftContent: {
            showMenuAction: true,
            items: true,
          },
          centerContent: { title: 'Desafios 1 a 1' },
        }}
        sidebar={() => (
          <div className="lg:w-[334px]">
            {Boolean(
              state.pendingList.length ||
                state.playingList.length ||
                state.invitationList.length
            ) && (
              <Divider
                className="mt-6 mb-7 h-[2px] lg:hidden"
                type="horizontal"
              />
            )}
            <SideBarContent
              init={state.init}
              list={state.recommendedList || []}
            />
          </div>
        )}
        tabs={() => isMobile && state.fechas && <MainTabs />}
      />
    </>
  );
};
